import { useState } from 'react';
import { notification } from 'antd';
import {
  useMfaSetupMutation,
  useMfaConfirmMutation,
} from '@/state/slices/auth';
import { IntroStep } from './IntroStep';
import { ScanStep } from './ScanStep';
import { BackupStep } from './BackupStep';

type SetupStep = 'intro' | 'scan' | 'backup';

interface MFASetupProps {
  onComplete: () => void;
  onCancel?: () => void;
}

export const MFASetup = ({ onComplete, onCancel }: MFASetupProps) => {
  const [step, setStep] = useState<SetupStep>('intro');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [secret, setSecret] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const [setupMfa, { isLoading: isSettingUp }] = useMfaSetupMutation();
  const [confirmMfa, { isLoading: isConfirming }] = useMfaConfirmMutation();
  const [api, contextHolder] = notification.useNotification();

  const handleStartSetup = async () => {
    try {
      const result = await setupMfa().unwrap();
      setProvisioningUri(result.provisioningUri);
      setSecret(result.secret);
      setStep('scan');
    } catch {
      api.error({ title: 'Failed to start MFA setup. Please try again.' });
    }
  };

  const handleConfirm = async () => {
    try {
      const result = await confirmMfa({ code: confirmCode }).unwrap();
      setBackupCodes(result.backupCodes);
      setStep('backup');
    } catch {
      api.error({ title: 'Invalid code. Please try again.' });
      setConfirmCode('');
    }
  };

  return (
    <>
      {contextHolder}
      {step === 'intro' && (
        <IntroStep
          onStart={handleStartSetup}
          onCancel={onCancel}
          isLoading={isSettingUp}
        />
      )}
      {step === 'scan' && (
        <ScanStep
          provisioningUri={provisioningUri}
          secret={secret}
          confirmCode={confirmCode}
          onCodeChange={setConfirmCode}
          onConfirm={handleConfirm}
          onCancel={onCancel}
          isLoading={isConfirming}
        />
      )}
      {step === 'backup' && (
        <BackupStep codes={backupCodes} onComplete={onComplete} />
      )}
    </>
  );
};
